import { mediaUrl } from '../../../lib/media-url.js';
import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { ShoppingCart, Check, Send, Package, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { cn, formatCurrency } from '../../../lib/utils.js';
import { displayUnitCents, priceDisplayMode, vatRateOf } from '../../../lib/price.js';
import { useCart } from '../../../store/cart.jsx';

export function VisualProductCard({
  product,
  tenant,
  onAddToCart,
  onViewProduct,
  onInquiry,
  index = 0,
  style,
  className,
  ...props
}) {
  let isInCartFn = () => false;
  let addItemFn = () => {};
  try {
    const cart = useCart();
    if (cart) {
      isInCartFn = cart.isInCart;
      addItemFn = cart.addItem;
    }
  } catch {}

  const sku = product?.sku || product?.id || '';
  const alreadyInCart = sku ? isInCartFn(sku) : false;

  let images = [];
  try {
    images = Array.isArray(product?.images)
      ? product.images
      : (product?.images_json ? JSON.parse(product.images_json) : []);
  } catch {
    images = [];
  }
  const primaryImage = (images && images[0]?.url) || (typeof images[0] === 'string' && images[0]) || product?.primaryImageUrl || product?.image_url || '';

  const unitPriceCents = product?.price?.cents !== undefined
    ? Number(product.price.cents)
    : (product?.base_price_cents !== undefined
      ? Number(product.base_price_cents)
      : (product?.base_price !== undefined ? Number(product.base_price) : (Number(product?.unitPriceCents) || 0)));

  const isBuyable = unitPriceCents > 0;
  // a-055b: product.price.formatted (Serverwert) bewusst ignoriert; Betrag aus price.cents, siehe src/lib/price.js
  const formattedPrice = formatCurrency(displayUnitCents(unitPriceCents, priceDisplayMode(tenant), vatRateOf(tenant)));

  const handleCardClick = () => {
    if (onViewProduct) {
      onViewProduct(product?.slug || product?.sku || product?.id);
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    } else {
      addItemFn(product, 1);
    }
  };

  const staggerDelay = `${Math.min(Math.max(0, index) * 20, 200)}ms`;

  return (
    <Card
      data-card-variant="visual_card"
      style={{
        ...style,
        animationDelay: staggerDelay,
        '--stagger-delay': staggerDelay,
      }}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-white transition-all duration-300 hover:border-[var(--primary)] hover:shadow-xl anim-card-stagger",
        className
      )}
      {...props}
    >
      {/* Prominent High-Visual Canvas */}
      <button
        type="button"
        onClick={handleCardClick}
        className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-gradient-to-b from-[var(--surface-container-low,#f8fafc)] to-[var(--surface-container-high,#f1f5f9)] p-6 text-left focus:outline-none cursor-pointer"
        aria-label={`${product?.name} Details anzeigen`}
      >
        {primaryImage ? (
          <img
            src={mediaUrl(primaryImage)}
            alt={product?.name || 'Produkt'}
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-[var(--secondary)]/40 gap-1.5">
            <Package className="h-10 w-10" />
            <span className="text-xs font-bold uppercase tracking-wider">ASL Hardware</span>
          </div>
        )}

        {/* Floating Badge */}
        {(product?.isFeatured || product?.is_featured) && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[var(--primary)] px-2.5 py-1 text-xs font-extrabold uppercase tracking-wider text-[var(--on-primary,white)] shadow-md">
            <Sparkles className="h-3 w-3" />
            <span>Empfehlung</span>
          </span>
        )}

        {/* SKU Chip Floating Bottom Right */}
        <span className="absolute bottom-2.5 right-3 rounded bg-black/60 px-1.5 py-0.5 font-mono text-xs font-bold text-white shadow-2xs">
          {sku}
        </span>
      </button>

      {/* Info Section */}
      <CardContent className="flex flex-1 flex-col justify-between p-5 space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs font-bold">
            <span className="font-extrabold uppercase tracking-wider text-[var(--primary,#e3000b)]">
              {product?.manufacturer || 'Fachhandel'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleCardClick}
            className="text-left font-display text-base font-bold leading-snug text-[var(--foreground)] hover:text-[var(--primary)] transition-colors line-clamp-2 cursor-pointer"
          >
            {product?.name || 'Unbenanntes Produkt'}
          </button>

          {(product?.shortDescription || product?.short_description) && (
            <p className="text-xs text-[var(--secondary)] line-clamp-2 leading-relaxed pt-1">
              {product?.shortDescription || product?.short_description}
            </p>
          )}
        </div>

        {/* Price & Full-Width CTA */}
        <div className="space-y-3 border-t border-[var(--border)] pt-4 mt-auto">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-[var(--secondary)]">
              {isBuyable ? 'Preis' : 'Status'}
            </span>
            <span className="font-display text-xl font-extrabold text-[var(--foreground)]">
              {isBuyable ? formattedPrice : 'Auf Anfrage'}
            </span>
          </div>

          {isBuyable ? (
            <Button
              variant={alreadyInCart ? 'secondary' : 'default'}
              className="w-full h-10 font-bold gap-2 rounded-xl shadow-xs cursor-pointer"
              onClick={handleAddToCart}
            >
              {alreadyInCart ? (
                <>
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>{isStripeCheckout(tenant) ? 'Im Warenkorb' : 'Im Anfragekorb'}</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  <span>{isStripeCheckout(tenant) ? 'In den Warenkorb hinzufügen' : 'In den Anfragekorb'}</span>
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full h-10 font-medium gap-2 rounded-xl cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (onInquiry) onInquiry(product);
                else handleCardClick();
              }}
            >
              <Send className="h-4 w-4" />
              <span>Angebot anfordern</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
